import { CommentSection } from "./CommentSection";
import SubscriberSection from "./SubscriberSection";
import { WRITE_OP } from "@avenueops/frontend-shared";
import { AssigneeValue, ClientProcessComment, ClientSubscriber, ClientTag, SQLGeneratedProcess } from "@avenueops/shared-types";
import { Card, Drawer, DrawerBody, IconButton, MarkdownViewer, OvalLoader, Tabs } from "@avenueops/web-components";
import { DynamicStatus, WorkflowProcessPriority } from "@prisma/client";
import { SizeIcon } from "@radix-ui/react-icons";
import { CopyAction } from "components/CopyableField";
import SetDynamicAssigneeDropdown from "components/SetDynamicAssigneeDropdown";
import PriorityButton from "components/signals/processPage/PriorityButton";
import RunDetailsTable from "components/signals/processPage/RunDetailsTable";
import StatusDropdown from "components/signals/processPage/StatusDropdown";
import ActionsDropdown from "components/signals/processPage/actions/ActionsDropdown";
import { ProcessPlaybook } from "components/monitorDetails/sections/PlaybookBuilder/ProcessPlaybook";
import TagsSection from "components/tags/TagsSection";
import { useAbilities } from "context/OrganizationContext";
import _ from "lodash";
import Link from "next/link";
import Skeleton from "react-loading-skeleton";
import { pagesPath } from "utils/$path";
import { useAssignProcessMutation } from "utils/state/processes";
import { trpc, trpc2 } from "utils/trpc";
import { useState } from "react";

interface Props {
  process: SQLGeneratedProcess;
  setProcess: (_: SQLGeneratedProcess | null) => void;
}

export type ActualWorkflowDataForTheDetailsPanelType = {
  id: string;
  title: string | null;
  message: string | null;
  dataDiff: any;
  workflowId: string;
  assigneeId: string | null;
  assignee: {
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    image: string | null;
  } | null;
  dynamicStatus: Pick<DynamicStatus, "id" | "color" | "label"> | null;
  tags: ClientTag[];
  comments: ClientProcessComment[];
  subscribers: ClientSubscriber[];
  priority: WorkflowProcessPriority | null;
};

export default function ProcessTableDetailsPanel({ process, setProcess }: Props): JSX.Element {
  const initialData: ActualWorkflowDataForTheDetailsPanelType = {
    id: process.id,
    title: process.title,
    message: process.message,
    dataDiff: process.dataDiff,
    workflowId: process.workflowId,
    assigneeId: process.assigneeId,
    priority: process.priority as WorkflowProcessPriority,
    assignee:
      process.assigneeFirstName && process.assigneeLastName && process.assigneeEmail && process.assigneeImage
        ? {
            firstName: process.assigneeFirstName,
            lastName: process.assigneeLastName,
            email: process.assigneeEmail,
            image: process.assigneeImage,
          }
        : null,
    dynamicStatus:
      process.statusId && process.statusLabel && process.statusColor
        ? {
            id: process.statusId,
            label: process.statusLabel,
            color: process.statusColor,
          }
        : null,
    // placeholders until we load the real data from the get call. We use isLoading to determine if that data has loaded fully.
    comments: [],
    tags: [],
    subscribers: [],
  };

  /**
   * In the ProcessTableDetailsPanel, we want to use the data from the corresponding process table row until such time as we have loaded the data
   * from the workflow get call. This causes typing issues, because the ClientProcess and SQLGeneratedProcess don't both load the same data fields.
   * However, the useQuery expects the initialData and returned data to be of the same type. As such, we create the
   * ActualWorkflowDataForTheDetailsPanelType. It creates a single type used in the details panel and enables us to have proper typing.
   */
  const { data: __rawWorkflowProcess, isLoading } = trpc.processes.get.useQuery(
    { processId: process.id },
    {
      initialData,
    },
  );

  const { isLoading: actionsAreLoading, data: actions } = trpc2.v2.processActions.listForSignal.useQuery({ signalId: process.id });

  let workflowProcess: ActualWorkflowDataForTheDetailsPanelType | undefined = __rawWorkflowProcess;

  const [selectedTab, setSelectedTab] = useState<"PLAYBOOK" | "DATA">("PLAYBOOK");

  const { data: steps, isLoading: stepsLoading } = trpc2.v2.playbookSteps.listStepsByProcess.useQuery(
    { processId: workflowProcess?.id! },
    { enabled: !!workflowProcess, refetchInterval: 200 },
  );

  const { data: groups, isLoading: groupsLoading } = trpc.processes.getPlaybookStepGroups.useQuery(
    { processId: workflowProcess?.id! },
    { enabled: !!workflowProcess, refetchInterval: 200 },
  );

  const { mutateAsync: assignUserMutation } = useAssignProcessMutation();
  const abilities = useAbilities();
  const canWriteTags = abilities.can(WRITE_OP, "WorkflowProcess", "tags");

  const QuickButtons = (
    <div className="flex flex-row justify-between w-full items-center pl-2">
      <div className="text-gray-950 font-medium flex-wrap">{process.title}</div>
      <div className="flex flex-row items-center">
        <CopyAction copyValue={`${window.location.origin}/signals/${process.id}`} />
        <Link href={pagesPath.signals._id(process.id).$url()} onClick={(e) => e.stopPropagation()} className="items-center align-center hover:text-gray-950">
          <IconButton Icon={SizeIcon} type="OUTLINE" onClick={() => {}} />
        </Link>
      </div>
    </div>
  );

  return (
    <Drawer
      secondaryElement={QuickButtons}
      open={process !== null}
      setOpen={(open: boolean) => {
        if (!open) {
          setProcess(null);
        }
      }}
      maxSize={1200}
    >
      <DrawerBody className="px-0">
        <div className="flex flex-col w-full h-full justify-between gap-5 text-gray-900">
          <div className="flex flex-col gap-4 py-2 px-4">
            <MarkdownViewer value={process.message ?? "No message"} />
            <div className="flex flex-col gap-2">
              <div className="flex flex-row flex-wrap gap-x-2 items-center">
                <div>
                  <PriorityButton processId={process.id} priority={workflowProcess?.priority ?? process.priority} size="SMALL" />
                </div>
                {workflowProcess?.dynamicStatus ? (
                  <div className="w-fit">
                    <StatusDropdown processId={process.id} status={workflowProcess.dynamicStatus} workflowId={process.workflowId} size="SMALL" type="BUTTON" />
                  </div>
                ) : null}
                {workflowProcess?.dynamicStatus ? (
                  <div className="w-fit">
                    <SetDynamicAssigneeDropdown
                      assignee={
                        workflowProcess.assigneeId && workflowProcess.assignee?.email
                          ? {
                              id: workflowProcess.assigneeId,
                              firstName: workflowProcess.assignee?.firstName,
                              lastName: workflowProcess.assignee.lastName,
                              email: workflowProcess.assignee.email,
                              image: workflowProcess.assignee.image,
                            }
                          : undefined
                      }
                      processId={workflowProcess.id}
                      mutation={(value: AssigneeValue) => assignUserMutation({ processId: process.id, assigneeInfo: value })}
                      size="SMALL"
                      type="BUTTON"
                    />
                  </div>
                ) : null}
                {workflowProcess?.id && workflowProcess?.tags && (
                  <TagsSection
                    readOnly={!abilities.can(WRITE_OP, "WorkflowProcess", "tags")}
                    tags={workflowProcess?.tags ?? []}
                    workflowId={workflowProcess?.workflowId}
                    parentObject={{ type: "PROCESS", ids: [workflowProcess?.id] }}
                    size="SMALL"
                  />
                )}
                {!actionsAreLoading && (actions?.length ?? 0) > 0 ? (
                  <div>
                    <ActionsDropdown signalId={process.id} isNotificationOnly={!process.statusId} type="BUTTON" size="SMALL" />
                  </div>
                ) : null}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <div className="font-medium text-sm">Subscribers</div>
              {isLoading ? <Skeleton count={2} /> : workflowProcess ? <SubscriberSection workflowProcess={workflowProcess} /> : null}
            </div>
            <div className="flex flex-col gap-2">
              <div className="font-medium text-sm">Data</div>
              <RunDetailsTable dataDiff={workflowProcess?.dataDiff as any[]} />
            </div>

            {steps && groups && steps.length > 1 ? (
              <div className="flex flex-col gap-2">
                {selectedTab === "PLAYBOOK" ? (
                  isLoading || groupsLoading ? (
                    <div className="self-center py-3">
                      <OvalLoader />
                    </div>
                  ) : (
                    <>
                      <div className="font-medium text-sm">Playbook</div>
                      <ProcessPlaybook steps={steps} groups={groups} />
                    </>
                  )
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="border-t border-cool-gray-300 bg-cool-gray-50 p-2 flex-1">
            <CommentSection processId={process.id} />
          </div>
        </div>
      </DrawerBody>
    </Drawer>
  );
}
